import { zodResolver } from '@hookform/resolvers/zod';
import {
  useGetScene,
  useCreateScene,
  useUpdateScene,
  useDeleteScene,
  useActivateScene,
  useDeactivateScene,
  getListScenesQueryKey,
  getGetSceneQueryKey,
  apiClient,
  getApiErrorMessage,
  type ScenesCreateSceneRequest,
  type ScenesSceneDeviceRequest,
  type ScenesSceneResponse,
  type DevicesDeviceState,
} from '@smarthome/api';
import { useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  Save,
  Trash2,
  Play,
  Square,
  Timer,
  RefreshCw,
  AlertCircle,
  Clock,
  MoreVertical,
  History,
  ArrowUpDown,
  Zap,
} from 'lucide-react';
import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { z } from 'zod';

import {
  Button,
  TextField,
  Toggle,
  Select,
  Badge,
  Modal,
  useToast,
  cn,
  IconButton,
  Dropdown,
} from '@/components/ui';
import {
  ActivateSceneDialog,
  SceneConditionEvaluation,
  SceneTargetsEditor,
  type SceneConditionEvaluationData,
} from '@/features/scenes/components';
import {
  ConditionBuilder,
  validateConditionNode,
  type ConditionNode,
} from '@/features/scenes/conditions/ConditionBuilder';
import {
  toBackendCondition,
  fromBackendCondition,
} from '@/features/scenes/conditions/conditionConverter';
import { hasIncludedProperties } from '@/features/scenes/targets';
import {
  formatDurationShort,
  inferBestUnit,
  toSeconds,
  fromSeconds,
  DURATION_UNIT_OPTIONS,
} from '@/features/scenes/utils/duration';

// Form schema
const sceneSchema = z.object({
  name: z.string().min(1, 'Name is required').max(150),
  description: z.string().max(255).optional(),
  enabled: z.boolean().optional(),
  activationMode: z.enum(['stateful', 'instant']).optional(),
  durationMode: z.enum(['none', 'fromActivation', 'fromTriggerEnd']).optional(),
  durationValue: z.number().min(1).optional().nullable(),
  durationUnit: z.enum(['seconds', 'minutes', 'hours']).optional(),
});

type SceneFormData = z.infer<typeof sceneSchema>;
type SceneFormSnapshot = {
  form: SceneFormData;
  targets: ScenesSceneDeviceRequest[];
  trigger: ConditionNode | null;
  preconditions: ConditionNode | null;
};

export function SceneDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { addToast } = useToast();

  const isCreateMode = !id || id === 'new';

  // Form state
  const [targets, setTargets] = useState<ScenesSceneDeviceRequest[]>([]);
  const [trigger, setTrigger] = useState<ConditionNode | null>(null);
  const [preconditions, setPreconditions] = useState<ConditionNode | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showActivateDialog, setShowActivateDialog] = useState(false);
  const [triggerError, setTriggerError] = useState<string | null>(null);
  const [conditionEvaluation, setConditionEvaluation] =
    useState<SceneConditionEvaluationData | null>(null);
  const [isRefreshingEvaluation, setIsRefreshingEvaluation] = useState(false);

  // Track dirty state for targets/trigger/preconditions
  const [targetsChanged, setTargetsChanged] = useState(false);
  const [triggerChanged, setTriggerChanged] = useState(false);
  const [preconditionsChanged, setPreconditionsChanged] = useState(false);

  // API queries
  const {
    data: scene,
    isLoading,
    isError,
  } = useGetScene(id!, {
    query: {
      enabled: !isCreateMode,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    },
  });

  // Form setup
  const {
    register,
    handleSubmit,
    control,
    watch,
    reset,
    getValues,
    formState: { errors, isDirty },
  } = useForm<SceneFormData>({
    resolver: zodResolver(sceneSchema),
    defaultValues: {
      name: '',
      description: '',
      enabled: true,
      activationMode: 'stateful',
      durationMode: 'none',
      durationValue: null,
      durationUnit: 'minutes',
    },
  });

  const durationMode = watch('durationMode');
  const activationMode = watch('activationMode');
  const isInstantMode = activationMode === 'instant';

  // Check if form has unsaved changes
  const hasUnsavedChanges = isDirty || targetsChanged || triggerChanged || preconditionsChanged;

  const savedSnapshotRef = useRef<SceneFormSnapshot | null>(null);
  const loadedSceneIdRef = useRef<string | null>(null);

  const buildSnapshot = useCallback((sourceScene: ScenesSceneResponse): SceneFormSnapshot => {
    const unit = sourceScene.durationSeconds
      ? inferBestUnit(sourceScene.durationSeconds)
      : 'minutes';
    const durationVal = sourceScene.durationSeconds
      ? fromSeconds(sourceScene.durationSeconds, unit)
      : null;

    return {
      form: {
        name: sourceScene.name ?? '',
        description: sourceScene.description ?? '',
        enabled: sourceScene.enabled ?? true,
        activationMode: (sourceScene.activationMode as 'stateful' | 'instant') ?? 'stateful',
        durationMode: sourceScene.durationSeconds
          ? ((sourceScene.durationMode as 'fromActivation' | 'fromTriggerEnd') ?? 'fromActivation')
          : 'none',
        durationValue: durationVal,
        durationUnit: unit,
      },
      targets:
        sourceScene.devices?.map(
          (
            d: { id?: string; state?: DevicesDeviceState; deactivationState?: DevicesDeviceState },
            i: number
          ) => ({
            id: d.id ?? '',
            state: d.state ?? {},
            deactivationState: d.deactivationState,
            orderIndex: i,
          })
        ) ?? [],
      trigger: fromBackendCondition(sourceScene.trigger),
      preconditions: fromBackendCondition(sourceScene.preconditions),
    };
  }, []);

  const applySnapshot = useCallback(
    (snapshot: SceneFormSnapshot) => {
      reset(snapshot.form);
      setTargets(snapshot.targets);
      setTrigger(snapshot.trigger);
      setPreconditions(snapshot.preconditions);
      setTargetsChanged(false);
      setTriggerChanged(false);
      setPreconditionsChanged(false);
    },
    [reset]
  );

  // Initialize form from scene data (only once per scene id)
  useEffect(() => {
    if (!scene) return;
    const sceneKey = scene.id ?? id ?? null;
    if (loadedSceneIdRef.current === sceneKey) {
      return;
    }

    const snapshot = buildSnapshot(scene);
    applySnapshot(snapshot);
    savedSnapshotRef.current = snapshot;
    loadedSceneIdRef.current = sceneKey;
  }, [scene, id, buildSnapshot, applySnapshot]);

  // Track changes to targets
  const handleTargetsChange = useCallback((newTargets: ScenesSceneDeviceRequest[]) => {
    setTargets(newTargets);
    setTargetsChanged(true);
  }, []);

  // Track changes to trigger
  const handleTriggerChange = useCallback((newTrigger: ConditionNode | null) => {
    setTrigger(newTrigger);
    setTriggerChanged(true);
  }, []);

  // Track changes to preconditions
  const handlePreconditionsChange = useCallback((newPreconditions: ConditionNode | null) => {
    setPreconditions(newPreconditions);
    setPreconditionsChanged(true);
  }, []);

  const fetchEvaluation = useCallback(
    async (showError: boolean) => {
      if (!id) return;
      setIsRefreshingEvaluation(true);
      try {
        const { data } = await apiClient.get<SceneConditionEvaluationData>(
          `/api/scenes/${id}/evaluation`
        );
        setConditionEvaluation(data ?? null);
      } catch (err) {
        if (showError) {
          addToast({
            message: getApiErrorMessage(err, 'Failed to refresh evaluation'),
            variant: 'error',
          });
        }
      } finally {
        setIsRefreshingEvaluation(false);
      }
    },
    [id, addToast]
  );

  const refreshEvaluation = useCallback(() => {
    void fetchEvaluation(true);
  }, [fetchEvaluation]);

  useEffect(() => {
    if (!scene) return;
    setConditionEvaluation(null);
    void fetchEvaluation(false);
  }, [scene, fetchEvaluation]);

  // Mutations
  const { mutate: createScene, isPending: isCreating } = useCreateScene({
    mutation: {
      onSuccess: (data) => {
        addToast({ message: 'Scene created', variant: 'success' });
        queryClient.invalidateQueries({ queryKey: getListScenesQueryKey() });
        navigate(`/scenes/${data?.id}`);
      },
      onError: (err) => {
        addToast({
          message: getApiErrorMessage(err, 'Failed to create scene'),
          variant: 'error',
        });
      },
    },
  });

  const { mutate: updateScene, isPending: isUpdating } = useUpdateScene({
    mutation: {
      onSuccess: () => {
        addToast({ message: 'Scene updated', variant: 'success' });
        queryClient.invalidateQueries({ queryKey: getListScenesQueryKey() });
        queryClient.invalidateQueries({ queryKey: getGetSceneQueryKey(id!) });
        const snapshot: SceneFormSnapshot = {
          form: getValues(),
          targets,
          trigger,
          preconditions,
        };
        savedSnapshotRef.current = snapshot;
        applySnapshot(snapshot);
      },
      onError: (err) => {
        addToast({
          message: getApiErrorMessage(err, 'Failed to update scene'),
          variant: 'error',
        });
      },
    },
  });

  const { mutate: deleteScene } = useDeleteScene({
    mutation: {
      onMutate: () => setIsDeleting(true),
      onSuccess: () => {
        addToast({ message: 'Scene deleted', variant: 'success' });
        queryClient.invalidateQueries({ queryKey: getListScenesQueryKey() });
        navigate('/scenes');
      },
      onError: (err) => {
        addToast({
          message: getApiErrorMessage(err, 'Failed to delete scene'),
          variant: 'error',
        });
        setIsDeleting(false);
      },
    },
  });

  const { mutate: activateScene, isPending: isActivating } = useActivateScene({
    mutation: {
      onSuccess: () => {
        const isInstant = scene?.activationMode === 'instant';
        addToast({ message: isInstant ? 'Scene executed' : 'Scene activated', variant: 'success' });
        queryClient.invalidateQueries({ queryKey: getGetSceneQueryKey(id!) });
        queryClient.invalidateQueries({ queryKey: getListScenesQueryKey() });
      },
      onError: (err) => {
        addToast({
          message: getApiErrorMessage(err, 'Failed to activate scene'),
          variant: 'error',
        });
      },
    },
  });

  const { mutate: deactivateScene, isPending: isDeactivating } = useDeactivateScene({
    mutation: {
      onSuccess: () => {
        addToast({ message: 'Scene deactivated', variant: 'info' });
        queryClient.invalidateQueries({ queryKey: getGetSceneQueryKey(id!) });
        queryClient.invalidateQueries({ queryKey: getListScenesQueryKey() });
      },
      onError: (err) => {
        addToast({
          message: getApiErrorMessage(err, 'Failed to deactivate scene'),
          variant: 'error',
        });
      },
    },
  });

  // Validate stop delay requires triggers
  useEffect(() => {
    if (durationMode === 'fromTriggerEnd' && !trigger) {
      setTriggerError('Stop delay duration requires triggers to be configured');
    } else {
      setTriggerError(null);
    }
  }, [durationMode, trigger]);

  // Validation: check if all targets have at least one property
  const targetsValidationError = useMemo(() => {
    if (targets.length === 0) return 'At least one device target is required';
    const invalidTarget = targets.find((t) => !hasIncludedProperties(t.state));
    if (invalidTarget) return 'Each device must have at least one property selected';
    return null;
  }, [targets]);

  // Submit handler
  const onSubmit = useCallback(
    (data: SceneFormData) => {
      // Validate targets
      if (targetsValidationError) {
        addToast({ message: targetsValidationError, variant: 'error' });
        return;
      }

      // Validate trigger condition if present
      if (trigger) {
        const triggerValidation = validateConditionNode(trigger, { allowHysteresis: true });
        if (triggerValidation) {
          addToast({ message: `Trigger error: ${triggerValidation}`, variant: 'error' });
          return;
        }
      }

      // Validate preconditions if present
      if (preconditions) {
        const preconditionsValidation = validateConditionNode(preconditions, {
          allowHysteresis: false,
        });
        if (preconditionsValidation) {
          addToast({ message: `Precondition error: ${preconditionsValidation}`, variant: 'error' });
          return;
        }
      }

      // Build request
      // Convert duration value + unit to seconds for the API
      const durationSeconds =
        data.durationMode !== 'none' && data.durationValue && data.durationUnit
          ? toSeconds(data.durationValue, data.durationUnit)
          : undefined;

      // For instant scenes, don't send duration settings
      const isInstant = data.activationMode === 'instant';
      const request: ScenesCreateSceneRequest = {
        name: data.name,
        description: data.description || undefined,
        enabled: data.enabled,
        activationMode: data.activationMode,
        devices: targets,
        durationMode: isInstant
          ? undefined
          : data.durationMode !== 'none'
            ? data.durationMode
            : undefined,
        durationSeconds: isInstant ? undefined : durationSeconds,
        trigger: toBackendCondition(trigger),
        preconditions: toBackendCondition(preconditions),
      };

      if (isCreateMode) {
        createScene({ data: request });
      } else {
        updateScene({ id: id!, data: request });
      }
    },
    [
      targets,
      trigger,
      preconditions,
      isCreateMode,
      id,
      createScene,
      updateScene,
      addToast,
      targetsValidationError,
    ]
  );

  const handleDelete = useCallback(() => {
    if (!id) return;
    deleteScene({ id });
    setShowDeleteConfirm(false);
  }, [id, deleteScene]);

  const handleQuickActivate = useCallback(() => {
    if (!id) return;
    activateScene({ id, data: {} });
  }, [id, activateScene]);

  const handleDeactivate = useCallback(() => {
    if (!id) return;
    deactivateScene({ id });
  }, [id, deactivateScene]);

  // Discard changes and revert to last saved state
  const handleDiscard = useCallback(() => {
    const snapshot = savedSnapshotRef.current ?? (scene ? buildSnapshot(scene) : null);
    if (!snapshot) return;
    applySnapshot(snapshot);
    savedSnapshotRef.current = snapshot;
  }, [scene, buildSnapshot, applySnapshot]);

  const isSaving = isCreating || isUpdating;
  const isActive = scene?.active;

  // Loading state for edit mode
  if (!isCreateMode && isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-gray-300 border-t-blue-500 dark:border-zinc-700" />
      </div>
    );
  }

  // Error state for edit mode
  if (!isCreateMode && isError) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <Link
            to="/scenes"
            className="rounded-lg p-1.5 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:text-zinc-500 dark:hover:bg-white/[0.04] dark:hover:text-white"
          >
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <h1 className="text-2xl font-semibold tracking-tight text-gray-900 dark:text-white">
            Scene Not Found
          </h1>
        </div>
        <div className="rounded-lg bg-red-50 p-6 text-center dark:bg-red-500/10">
          <AlertCircle className="mx-auto mb-3 h-8 w-8 text-red-500" />
          <p className="text-sm text-red-600 dark:text-red-400">
            The scene could not be found. It may have been deleted.
          </p>
          <Link
            to="/scenes"
            className="mt-4 inline-block text-sm text-blue-500 hover:text-blue-400"
          >
            Back to scenes
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        {/* Left: Back + Title + Status chips */}
        <div className="flex items-center gap-3">
          <Link
            to="/scenes"
            className="rounded-lg p-1.5 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:text-zinc-500 dark:hover:bg-white/[0.04] dark:hover:text-white"
          >
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-2xl font-semibold tracking-tight text-gray-900 dark:text-white">
              {isCreateMode ? 'New Scene' : (scene?.name ?? 'Edit Scene')}
            </h1>
            {/* Status chips */}
            {!isCreateMode && (
              <div className="flex flex-wrap items-center gap-2">
                {scene?.activationMode === 'instant' && (
                  <Badge variant="default" size="sm">
                    <Zap className="mr-1 h-3 w-3" />
                    Instant
                  </Badge>
                )}
                {isActive && (
                  <Badge variant="info" size="sm" dot>
                    Active
                  </Badge>
                )}
                {scene?.enabled === false && (
                  <Badge variant="default" size="sm">
                    Disabled
                  </Badge>
                )}
                {scene?.priority !== undefined && (
                  <Badge variant="info" size="sm">
                    Priority {scene.priority}
                  </Badge>
                )}
                {scene?.durationSeconds && (
                  <Badge variant="default" size="sm">
                    {formatDurationShort(scene.durationSeconds)}
                  </Badge>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Right: State-driven actions */}
        <div className="flex flex-wrap items-center gap-2">
          {isCreateMode ? (
            /* Create mode: always show Cancel + Create */
            <>
              <Button variant="secondary" onClick={() => navigate('/scenes')}>
                Cancel
              </Button>
              <Button
                type="submit"
                form="scene-form"
                loading={isSaving}
                leftIcon={<Save className="h-4 w-4" />}
                disabled={!!targetsValidationError}
              >
                Create
              </Button>
            </>
          ) : hasUnsavedChanges ? (
            /* Edit mode with unsaved changes: Save + Discard are primary */
            <>
              <Button variant="ghost" onClick={handleDiscard}>
                Discard changes
              </Button>
              <Button
                type="submit"
                form="scene-form"
                loading={isSaving}
                leftIcon={<Save className="h-4 w-4" />}
                disabled={!!targetsValidationError}
              >
                Save
              </Button>
              {/* Activation controls de-emphasized (in overflow) */}
              <Dropdown
                trigger={<IconButton icon={<MoreVertical />} label="More options" />}
                align="right"
                onSelect={(value) => {
                  if (value === 'activate') {
                    setShowActivateDialog(true);
                  } else if (value === 'reapply') {
                    handleQuickActivate();
                  } else if (value === 'deactivate') {
                    handleDeactivate();
                  } else if (value === 'history') {
                    navigate(`/scenes/${id}/history`);
                  } else if (value === 'reorder') {
                    navigate('/scenes/reorder');
                  } else if (value === 'delete') {
                    setShowDeleteConfirm(true);
                  }
                }}
                items={[
                  ...(isActive
                    ? [
                        {
                          value: 'reapply',
                          label: 'Re-apply',
                          icon: <RefreshCw className="h-4 w-4" />,
                          description: 'Re-send scene states',
                        },
                        {
                          value: 'deactivate',
                          label: 'Deactivate',
                          icon: <Square className="h-4 w-4" />,
                          description: 'Save changes first to activate',
                        },
                      ]
                    : [
                        {
                          value: 'activate',
                          label: 'Activate...',
                          icon: <Play className="h-4 w-4" />,
                          description: 'Save changes first',
                        },
                      ]),
                  { type: 'divider' as const },
                  {
                    value: 'history',
                    label: 'Activation history',
                    icon: <History className="h-4 w-4" />,
                  },
                  {
                    value: 'reorder',
                    label: 'Reorder priorities',
                    icon: <ArrowUpDown className="h-4 w-4" />,
                  },
                  { type: 'divider' as const },
                  {
                    value: 'delete',
                    label: 'Delete...',
                    icon: <Trash2 className="h-4 w-4" />,
                    variant: 'danger' as const,
                  },
                ]}
              />
            </>
          ) : (
            /* Edit mode without unsaved changes: Activation is primary */
            <>
              {scene?.activationMode === 'instant' ? (
                // Instant scenes: simple Run button, no timer options
                <Button
                  variant="primary"
                  onClick={handleQuickActivate}
                  loading={isActivating}
                  leftIcon={<Zap className="h-4 w-4" />}
                >
                  Run
                </Button>
              ) : isActive ? (
                <div className="flex">
                  <Button
                    variant="secondary"
                    onClick={handleDeactivate}
                    loading={isDeactivating}
                    leftIcon={<Square className="h-4 w-4" />}
                    className="rounded-r-none"
                  >
                    Deactivate
                  </Button>
                  <button
                    type="button"
                    onClick={() => setShowActivateDialog(true)}
                    className="inline-flex h-9 items-center justify-center rounded-r-lg border-l border-gray-300 bg-gray-200 px-2.5 text-gray-700 transition-colors hover:bg-gray-300 hover:text-gray-900 dark:border-white/[0.06] dark:bg-zinc-800 dark:text-zinc-300 dark:hover:bg-zinc-700 dark:hover:text-white"
                  >
                    <Timer className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <div className="flex">
                  <Button
                    variant="primary"
                    onClick={handleQuickActivate}
                    loading={isActivating}
                    leftIcon={<Play className="h-4 w-4" />}
                    className="rounded-r-none"
                  >
                    Activate
                  </Button>
                  <button
                    type="button"
                    onClick={() => setShowActivateDialog(true)}
                    className="inline-flex h-9 items-center justify-center rounded-r-lg border-l border-blue-500 bg-blue-600 px-2.5 text-white transition-colors hover:bg-blue-500"
                  >
                    <Timer className="h-4 w-4" />
                  </button>
                </div>
              )}
              {/* Overflow menu */}
              <Dropdown
                trigger={<IconButton icon={<MoreVertical />} label="More options" />}
                align="right"
                onSelect={(value) => {
                  if (value === 'reapply') {
                    handleQuickActivate();
                  } else if (value === 'history') {
                    navigate(`/scenes/${id}/history`);
                  } else if (value === 'reorder') {
                    navigate('/scenes/reorder');
                  } else if (value === 'delete') {
                    setShowDeleteConfirm(true);
                  }
                }}
                items={[
                  ...(isActive
                    ? [
                        {
                          value: 'reapply',
                          label: 'Re-apply',
                          icon: <RefreshCw className="h-4 w-4" />,
                          description: 'Re-send scene states',
                        },
                        { type: 'divider' as const },
                      ]
                    : []),
                  {
                    value: 'history',
                    label: 'Activation history',
                    icon: <History className="h-4 w-4" />,
                  },
                  {
                    value: 'reorder',
                    label: 'Reorder priorities',
                    icon: <ArrowUpDown className="h-4 w-4" />,
                  },
                  { type: 'divider' },
                  {
                    value: 'delete',
                    label: 'Delete...',
                    icon: <Trash2 className="h-4 w-4" />,
                    variant: 'danger',
                  },
                ]}
              />
            </>
          )}
        </div>
      </div>

      {/* Main content - single column */}
      <form id="scene-form" onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Basics & Settings card - with clear visual groupings */}
        <div className="rounded-xl border border-gray-200 bg-white dark:border-white/[0.06] dark:bg-[#18181b]">
          {/* Card header */}
          <div className="border-b border-gray-100 px-5 py-4 dark:border-white/[0.04]">
            <h2 className="text-base font-medium text-gray-900 dark:text-white">
              Basics & Settings
            </h2>
          </div>

          <div className="p-5">
            {/* 2-column grid on desktop */}
            <div className="grid gap-8 lg:grid-cols-[1fr_280px]">
              {/* Left column: Identity */}
              <div className="space-y-4">
                <TextField
                  label="Name"
                  {...register('name')}
                  error={errors.name?.message}
                  placeholder="e.g., Good Morning"
                />
                <TextField
                  label="Description"
                  {...register('description')}
                  error={errors.description?.message}
                  placeholder="Optional description"
                />
              </div>

              {/* Right column: Scene Behavior - visually grouped */}
              <div className="rounded-lg border border-gray-100 bg-gray-50/50 p-4 dark:border-white/[0.04] dark:bg-white/[0.02]">
                <div className="mb-3 text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-zinc-500">
                  Scene Behavior
                </div>
                <div className="space-y-4">
                  <Controller
                    name="activationMode"
                    control={control}
                    render={({ field }) => (
                      <Select
                        label="Activation mode"
                        value={field.value ?? 'stateful'}
                        onChange={field.onChange}
                        options={[
                          { value: 'stateful', label: 'Stateful' },
                          { value: 'instant', label: 'Instant' },
                        ]}
                        fullWidth
                      />
                    )}
                  />

                  {isInstantMode && (
                    <div className="flex items-start gap-2 rounded-lg bg-blue-50 px-3 py-2.5 text-sm text-blue-700 dark:bg-blue-500/10 dark:text-blue-400">
                      <Zap className="mt-0.5 h-4 w-4 flex-shrink-0" />
                      <span>
                        Instant scenes execute once and complete immediately. They don&apos;t have
                        duration or restore options.
                      </span>
                    </div>
                  )}

                  <div className="border-t border-gray-200 pt-4 dark:border-white/[0.06]">
                    <Controller
                      name="enabled"
                      control={control}
                      render={({ field }) => (
                        <Toggle
                          label="Auto-trigger"
                          description="Trigger by conditions"
                          checked={field.value ?? true}
                          onChange={field.onChange}
                          layout="switch-right"
                        />
                      )}
                    />
                  </div>

                  {!isInstantMode && (
                    <>
                      <div className="border-t border-gray-200 pt-4 dark:border-white/[0.06]">
                        <Controller
                          name="durationMode"
                          control={control}
                          render={({ field }) => (
                            <Select
                              label="Duration"
                              value={field.value ?? 'none'}
                              onChange={field.onChange}
                              options={[
                                { value: 'none', label: 'No duration' },
                                { value: 'fromActivation', label: 'Fixed duration' },
                                { value: 'fromTriggerEnd', label: 'Stop delay' },
                              ]}
                              fullWidth
                            />
                          )}
                        />

                        {durationMode && durationMode !== 'none' && (
                          <div className="mt-3 flex gap-2">
                            <Controller
                              name="durationValue"
                              control={control}
                              render={({ field }) => (
                                <TextField
                                  label="Value"
                                  hideLabel
                                  type="number"
                                  min={1}
                                  value={field.value ?? ''}
                                  onChange={(e) =>
                                    field.onChange(e.target.value ? Number(e.target.value) : null)
                                  }
                                  placeholder="30"
                                  icon={<Clock className="h-4 w-4" />}
                                />
                              )}
                            />
                            <Controller
                              name="durationUnit"
                              control={control}
                              render={({ field }) => (
                                <Select
                                  label="Unit"
                                  hideLabel
                                  value={field.value ?? 'minutes'}
                                  onChange={field.onChange}
                                  options={DURATION_UNIT_OPTIONS}
                                  className="w-28"
                                />
                              )}
                            />
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Device Targets */}
        <div className="rounded-xl border border-gray-200 bg-white p-5 dark:border-white/[0.06] dark:bg-[#18181b]">
          <h2 className="mb-4 text-base font-medium text-gray-900 dark:text-white">
            Device Targets
          </h2>
          <SceneTargetsEditor
            value={targets}
            onChange={handleTargetsChange}
            error={targetsValidationError ?? undefined}
          />
        </div>

        {/* Automation (Triggers + Preconditions with tabs) */}
        <AutomationCard
          trigger={trigger}
          preconditions={preconditions}
          onTriggerChange={handleTriggerChange}
          onPreconditionsChange={handlePreconditionsChange}
          triggerError={triggerError}
        />

        {scene && (
          <SceneConditionEvaluation
            evaluation={conditionEvaluation}
            isActive={Boolean(isActive)}
            onRefresh={refreshEvaluation}
            isRefreshing={isRefreshingEvaluation}
          />
        )}
      </form>

      {/* Delete confirmation modal */}
      <Modal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        title="Delete Scene"
        subtitle="This action cannot be undone"
        footer={
          <>
            <Button variant="ghost" onClick={() => setShowDeleteConfirm(false)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={handleDelete} loading={isDeleting}>
              Delete Scene
            </Button>
          </>
        }
      >
        <p className="text-sm text-gray-600 dark:text-zinc-400">
          Are you sure you want to delete &quot;{scene?.name}&quot;? This will permanently remove
          the scene and all its configuration.
        </p>
      </Modal>

      {/* Activate dialog */}
      {scene && (
        <ActivateSceneDialog
          scene={scene}
          isOpen={showActivateDialog}
          onClose={() => setShowActivateDialog(false)}
          isAlreadyActive={isActive}
        />
      )}
    </div>
  );
}

// Automation card with tabs for Triggers and Preconditions
interface AutomationCardProps {
  trigger: ConditionNode | null;
  preconditions: ConditionNode | null;
  onTriggerChange: (trigger: ConditionNode | null) => void;
  onPreconditionsChange: (preconditions: ConditionNode | null) => void;
  triggerError: string | null;
}

function AutomationCard({
  trigger,
  preconditions,
  onTriggerChange,
  onPreconditionsChange,
  triggerError,
}: AutomationCardProps) {
  const [activeTab, setActiveTab] = useState<'triggers' | 'preconditions'>('triggers');

  // Count conditions for badge display
  const triggerCount = trigger ? countConditions(trigger) : 0;
  const preconditionsCount = preconditions ? countConditions(preconditions) : 0;

  return (
    <div className="rounded-xl border border-gray-200 bg-white dark:border-white/[0.06] dark:bg-[#18181b]">
      {/* Tab header */}
      <div className="flex border-b border-gray-200 dark:border-white/[0.06]">
        <button
          type="button"
          onClick={() => setActiveTab('triggers')}
          className={cn(
            'flex items-center gap-2 px-5 py-3 text-sm font-medium transition-colors',
            activeTab === 'triggers'
              ? 'border-b-2 border-blue-500 text-blue-600 dark:text-blue-400'
              : 'text-gray-500 hover:text-gray-700 dark:text-zinc-500 dark:hover:text-zinc-300'
          )}
        >
          Triggers
          {triggerCount > 0 && (
            <span
              className={cn(
                'flex h-5 min-w-[20px] items-center justify-center rounded-full px-1.5 text-xs font-medium',
                activeTab === 'triggers'
                  ? 'bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-400'
                  : 'bg-gray-100 text-gray-600 dark:bg-zinc-700 dark:text-zinc-400'
              )}
            >
              {triggerCount}
            </span>
          )}
          {triggerError && <AlertCircle className="h-4 w-4 text-red-500" />}
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('preconditions')}
          className={cn(
            'flex items-center gap-2 px-5 py-3 text-sm font-medium transition-colors',
            activeTab === 'preconditions'
              ? 'border-b-2 border-blue-500 text-blue-600 dark:text-blue-400'
              : 'text-gray-500 hover:text-gray-700 dark:text-zinc-500 dark:hover:text-zinc-300'
          )}
        >
          <span className="text-gray-400 dark:text-zinc-600">Preconditions</span>
          <span className="text-xs text-gray-400 dark:text-zinc-600">(Advanced)</span>
          {preconditionsCount > 0 && (
            <span
              className={cn(
                'flex h-5 min-w-[20px] items-center justify-center rounded-full px-1.5 text-xs font-medium',
                activeTab === 'preconditions'
                  ? 'bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-400'
                  : 'bg-gray-100 text-gray-600 dark:bg-zinc-700 dark:text-zinc-400'
              )}
            >
              {preconditionsCount}
            </span>
          )}
        </button>
      </div>

      {/* Tab content */}
      <div className="p-5">
        {activeTab === 'triggers' ? (
          <>
            <p className="mb-4 text-sm text-gray-500 dark:text-zinc-500">
              Conditions that activate this scene when they become true
            </p>
            <ConditionBuilder
              value={trigger}
              onChange={onTriggerChange}
              error={triggerError ?? undefined}
              allowHysteresis
            />
          </>
        ) : (
          <>
            <p className="mb-4 text-sm text-gray-500 dark:text-zinc-500">
              Additional conditions that must be true for activation to proceed
            </p>
            {!preconditions && (
              <div className="mb-4 rounded-lg bg-gray-50 px-3 py-2 text-sm text-gray-600 dark:bg-zinc-800/50 dark:text-zinc-400">
                Preconditions are optional. Use them to restrict when triggers can activate the
                scene.
              </div>
            )}
            <ConditionBuilder value={preconditions} onChange={onPreconditionsChange} />
          </>
        )}
      </div>
    </div>
  );
}

// Helper to count conditions in a node
function countConditions(node: ConditionNode): number {
  if (node.all) return node.all.reduce((sum, c) => sum + countConditions(c), 0);
  if (node.any) return node.any.reduce((sum, c) => sum + countConditions(c), 0);
  if (node.not) return countConditions(node.not);
  if (node.compare || node.timeBetween) return 1;
  return 0;
}
